import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API, { formatError } from "@/api";
import { toast } from "sonner";
import {
    Plus, Trash2, KanbanSquare, Pencil, Check,
    Users, Briefcase, Code2, Palette, Megaphone, Wrench, Globe, Zap, Target,
    Shield, Rocket, Database, Cpu, Headphones, Star, BarChart2, ClipboardList,
    Calendar, FolderOpen, Layers, Package, Activity, FileText, ShoppingCart,
    Crown, StarOff, Search, X
} from "lucide-react";

const ENTITY_ICONS = [
    { key: "users", Icon: Users }, { key: "briefcase", Icon: Briefcase },
    { key: "code", Icon: Code2 }, { key: "palette", Icon: Palette },
    { key: "megaphone", Icon: Megaphone }, { key: "wrench", Icon: Wrench },
    { key: "globe", Icon: Globe }, { key: "zap", Icon: Zap },
    { key: "target", Icon: Target }, { key: "shield", Icon: Shield },
    { key: "rocket", Icon: Rocket }, { key: "database", Icon: Database },
    { key: "cpu", Icon: Cpu }, { key: "headphones", Icon: Headphones },
    { key: "star", Icon: Star }, { key: "bar_chart", Icon: BarChart2 },
    { key: "clipboard", Icon: ClipboardList }, { key: "calendar", Icon: Calendar },
    { key: "folder", Icon: FolderOpen }, { key: "layers", Icon: Layers },
    { key: "package", Icon: Package }, { key: "activity", Icon: Activity },
    { key: "kanban", Icon: KanbanSquare }, { key: "file_text", Icon: FileText },
    { key: "shopping_cart", Icon: ShoppingCart },
];

function getEntityIcon(iconKey, DefaultIcon, size = 20) {
    const found = ENTITY_ICONS.find(i => i.key === iconKey);
    const IconComp = (found?.Icon) || DefaultIcon;
    return <IconComp size={size} />;
}

function IconPicker({ value, onChange }) {
    return (
        <div>
            <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-2">Pictogramă</label>
            <div className="grid grid-cols-9 gap-1.5">
                {ENTITY_ICONS.map(({ key, Icon }) => (
                    <button key={key} type="button" onClick={() => onChange(key)}
                        data-testid={`icon-pick-${key}`}
                        className={`w-8 h-8 rounded-md flex items-center justify-center transition-all
                            ${value === key ? "bg-[#002FA7] text-white shadow-sm" : "bg-[#F8F9FA] text-[#6B7280] hover:bg-[#EEF2FF] hover:text-[#002FA7]"}`}>
                        <Icon size={15} />
                    </button>
                ))}
            </div>
        </div>
    );
}

import { displayRole, ROLE_LABELS as GLOBAL_ROLE_LABELS } from "@/lib/roles";

const ROLE_LABELS = {
    ...GLOBAL_ROLE_LABELS,
    lead: "Lead",
};

export default function TeamPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdmin = ["super_admin", "admin"].includes(user?.role);
    const [team, setTeam] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showAddMember, setShowAddMember] = useState(false);
    const [showEditTeam, setShowEditTeam] = useState(false);
    const [allUsers, setAllUsers] = useState([]);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [memberSearch, setMemberSearch] = useState("");
    const [editForm, setEditForm] = useState({ name: "", description: "", icon: "users", lead_id: "" });
    const [saving, setSaving] = useState(false);
    const [detailedMembers, setDetailedMembers] = useState([]);
    const [showDetailed, setShowDetailed] = useState(false);

    const isLedByMe = !!team?.is_led_by_me;
    // Backend ne dă can_manage (admin, lead direct, sau workspace-lead în același ws)
    const canManage = isAdmin || isLedByMe || team?.can_manage === true;
    const canDelete = isAdmin; // doar admin poate șterge echipa
    const canChangeLead = isAdmin; // doar admin poate schimba lead-ul echipei

    const fetchTeam = () => {
        API.get(`/teams/${id}`)
            .then(r => {
                setTeam(r.data);
                setEditForm({ name: r.data.name, description: r.data.description || "", icon: r.data.icon || "users", lead_id: r.data.lead_id || "" });
            })
            .catch(() => toast.error("Echipă negăsită"))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchTeam();
        API.get("/users").then(r => setAllUsers(r.data)).catch(() => {});
    }, [id]);

    const fetchDetailed = () => {
        if (!canManage) return;
        API.get(`/teams/${id}/members-detailed`).then(r => setDetailedMembers(r.data)).catch(() => {});
    };

    useEffect(() => {
        if (showDetailed && canManage) fetchDetailed();
        // eslint-disable-next-line
    }, [showDetailed, id, canManage]);

    const addMember = async (e) => {
        e.preventDefault();
        if (selectedUserIds.length === 0) return;
        setSaving(true);
        try {
            const res = await API.post(`/teams/${id}/members`, { user_ids: selectedUserIds });
            const addedCount = res.data?.added?.length ?? selectedUserIds.length;
            toast.success(addedCount === 1 ? "Membru adăugat" : `${addedCount} membri adăugați`);
            setShowAddMember(false);
            setSelectedUserIds([]);
            setMemberSearch("");
            fetchTeam();
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSaving(false);
        }
    };

    const toggleUserSelection = (userId) => {
        setSelectedUserIds(prev =>
            prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
        );
    };

    const removeMember = async (userId) => {
        if (!window.confirm("Elimini membrul din echipă?")) return;
        try {
            await API.delete(`/teams/${id}/members/${userId}`);
            toast.success("Membru eliminat");
            fetchTeam();
        } catch (err) {
            toast.error(formatError(err));
        }
    };

    const setAsLead = async (userId) => {
        try {
            await API.post(`/teams/${id}/set-lead`, { user_id: userId });
            toast.success("Team Lead actualizat");
            fetchTeam();
        } catch (err) {
            toast.error(formatError(err));
        }
    };

    const unsetLead = async () => {
        if (!window.confirm("Elimini Team Lead-ul actual? Echipa va rămâne fără lead.")) return;
        try {
            await API.post(`/teams/${id}/set-lead`, { user_id: "" });
            toast.success("Team Lead eliminat");
            fetchTeam();
        } catch (err) {
            toast.error(formatError(err));
        }
    };

    const saveTeam = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { workspace_id: team.workspace_id, name: editForm.name, description: editForm.description, icon: editForm.icon };
            // Include lead_id doar dacă userul e admin și selecția s-a schimbat (sau e completă)
            if (isAdmin && (editForm.lead_id || "") !== (team?.lead_id || "")) {
                // Backend: lead_id e optional; trimit doar dacă e setat; dacă vreau "fără lead", folosesc endpoint separat
                if (editForm.lead_id) {
                    payload.lead_id = editForm.lead_id;
                } else {
                    // unset via set-lead endpoint
                    await API.post(`/teams/${id}/set-lead`, { user_id: "" });
                }
            }
            await API.patch(`/teams/${id}`, payload);
            toast.success("Echipă actualizată");
            setShowEditTeam(false);
            fetchTeam();
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSaving(false);
        }
    };

    const deleteTeam = async () => {
        if (!window.confirm(`Ștergi echipa "${team?.name}"? Această acțiune nu poate fi anulată.`)) return;
        try {
            await API.delete(`/teams/${id}`);
            toast.success("Echipă ștearsă");
            navigate(-1);
        } catch (err) {
            toast.error(formatError(err));
        }
    };

    const existingMemberIds = new Set(team?.members?.map(m => m.id) || []);
    const availableUsers = allUsers.filter(u => !existingMemberIds.has(u.id));

    if (loading) return (
        <div className="p-6 space-y-4" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            {[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-white border border-[#E4E4E7] rounded-md animate-pulse" />)}
        </div>
    );

    return (
        <div className="p-6 max-w-4xl mx-auto" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-50 rounded-md flex items-center justify-center text-[#002FA7]">
                        {getEntityIcon(team?.icon, Users, 20)}
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{team?.name}</h1>
                            {canManage && (
                                <div className="flex items-center gap-1">
                                    <button onClick={() => setShowEditTeam(true)} data-testid="edit-team-btn"
                                        className="text-[#9CA3AF] hover:text-[#002FA7] p-1 rounded hover:bg-blue-50 transition-colors">
                                        <Pencil size={13} />
                                    </button>
                                    {canDelete && (
                                        <button onClick={deleteTeam} data-testid="delete-team-btn"
                                            className="text-[#9CA3AF] hover:text-red-500 p-1 rounded hover:bg-red-50 transition-colors">
                                            <Trash2 size={13} />
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                        {team?.description && <p className="text-sm text-[#9CA3AF] mt-0.5">{team.description}</p>}
                    </div>
                </div>
                {canManage && (
                    <button onClick={() => setShowAddMember(true)} data-testid="add-member-btn"
                        className="flex items-center gap-1.5 text-xs bg-[#002FA7] text-white px-3 py-2 rounded-md hover:bg-[#002482] transition-colors">
                        <Plus size={14} /> Adaugă membru
                    </button>
                )}
            </div>

            {/* Members */}
            <div className="bg-white border border-[#E4E4E7] rounded-md mb-6" data-testid="team-members-section">
                <div className="px-5 py-4 border-b border-[#E4E4E7]">
                    <h2 className="font-semibold text-sm text-[#111827]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                        Membri ({team?.members?.length || 0})
                    </h2>
                </div>
                <div className="divide-y divide-[#F1F3F5]">
                    {(team?.members || []).map(member => (
                        <div key={member.id} className={`flex items-center gap-4 px-5 py-3 transition-colors ${member.id === team?.lead_id ? "bg-gradient-to-r from-amber-50/50 to-transparent" : "hover:bg-[#F8F9FA]"}`} data-testid={`member-row-${member.id}`}>
                            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 relative"
                                style={{ backgroundColor: member.avatar || "#002FA7" }}>
                                <span className="text-white text-sm font-bold">{member.name?.[0]?.toUpperCase()}</span>
                                {member.id === team?.lead_id && (
                                    <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-amber-500 flex items-center justify-center" title="Team Lead">
                                        <Crown size={9} className="text-white" />
                                    </div>
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-[#111827]">{member.nickname || member.name}</p>
                                <p className="text-xs text-[#9CA3AF]">{member.job_title || member.email}</p>
                            </div>
                            {member.id === team?.lead_id ? (
                                <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-sm font-bold uppercase tracking-wide flex items-center gap-1" data-testid={`lead-badge-${member.id}`}>
                                    <Crown size={10} /> Team Lead
                                </span>
                            ) : (
                                <span className="text-xs bg-blue-50 text-[#002FA7] px-2 py-0.5 rounded-sm font-semibold" title={`Rol global: ${ROLE_LABELS[member.role] || member.role}`}>
                                    {displayRole(member)}
                                </span>
                            )}
                            {isAdmin && member.role !== "super_admin" && member.id !== team?.lead_id && (
                                <button onClick={() => setAsLead(member.id)} data-testid={`promote-lead-${member.id}`}
                                    title="Fă Team Lead"
                                    className="text-[#9CA3AF] hover:text-amber-500 transition-colors p-1 rounded hover:bg-amber-50">
                                    <Star size={14} />
                                </button>
                            )}
                            {isAdmin && member.id === team?.lead_id && (
                                <button onClick={unsetLead} data-testid={`demote-lead-${member.id}`}
                                    title="Elimină Team Lead"
                                    className="text-amber-600 hover:text-[#9CA3AF] transition-colors p-1 rounded hover:bg-gray-50">
                                    <StarOff size={14} />
                                </button>
                            )}
                            {canManage && (() => {
                                // Team lead nu poate scoate: admin, super_admin, lead-ul echipei, sau un alt lead
                                const isProtected = !isAdmin && (
                                    ["super_admin", "admin"].includes(member.role) ||
                                    member.id === team?.lead_id ||
                                    member.team_role === "lead"
                                );
                                if (isProtected) return null;
                                return (
                                    <button onClick={() => removeMember(member.id)} data-testid={`remove-member-${member.id}`}
                                        className="text-[#9CA3AF] hover:text-red-500 transition-colors p-1 rounded hover:bg-red-50">
                                        <Trash2 size={14} />
                                    </button>
                                );
                            })()}
                        </div>
                    ))}
                    {(team?.members || []).length === 0 && (
                        <div className="py-10 text-center text-sm text-[#9CA3AF]">Niciun membru</div>
                    )}
                </div>
            </div>

            {/* Detailed members view — admin + team_lead of this team */}
            {canManage && (
                <div className="bg-white border border-[#E4E4E7] rounded-md mb-6" data-testid="team-members-detailed-section">
                    <button onClick={() => setShowDetailed(!showDetailed)}
                        data-testid="team-toggle-detailed-members"
                        className="w-full flex items-center justify-between px-5 py-4 border-b border-[#E4E4E7] hover:bg-[#F8F9FA]">
                        <div className="flex items-center gap-2">
                            <Users size={16} className="text-[#002FA7]" />
                            <h2 className="font-semibold text-sm text-[#111827]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                                Membri detaliați (vedere cu taskuri)
                            </h2>
                        </div>
                        <span className="text-xs text-[#9CA3AF]">{showDetailed ? "Ascunde" : "Arată"}</span>
                    </button>
                    {showDetailed && (
                        <div className="divide-y divide-[#F1F3F5]">
                            {detailedMembers.map(m => (
                                <div key={m.id} className="p-5" data-testid={`detailed-member-${m.id}`}>
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: m.avatar || "#002FA7" }}>
                                            <span className="text-white text-sm font-bold">{m.name?.[0]?.toUpperCase()}</span>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-bold text-[#111827]">{m.name}</p>
                                            <p className="text-xs text-[#9CA3AF]">{m.job_title || m.email}</p>
                                        </div>
                                        <div className="flex gap-4 text-xs">
                                            <div className="text-center">
                                                <div className="font-bold text-[#002FA7] text-sm">{m.task_stats?.active || 0}</div>
                                                <div className="text-[10px] text-[#9CA3AF] uppercase">Active</div>
                                            </div>
                                            <div className="text-center">
                                                <div className="font-bold text-red-600 text-sm">{m.task_stats?.overdue || 0}</div>
                                                <div className="text-[10px] text-[#9CA3AF] uppercase">Întârziate</div>
                                            </div>
                                            <div className="text-center">
                                                <div className="font-bold text-green-600 text-sm">{m.task_stats?.completed || 0}</div>
                                                <div className="text-[10px] text-[#9CA3AF] uppercase">Finalizate</div>
                                            </div>
                                        </div>
                                    </div>
                                    {(m.tasks || []).length === 0 ? (
                                        <p className="text-xs text-[#9CA3AF] italic pl-13">Niciun task</p>
                                    ) : (
                                        <div className="space-y-1 mt-2">
                                            {m.tasks.slice(0, 8).map(t => {
                                                const overdue = t.due_date && new Date(t.due_date) < new Date() && !t.completed_at;
                                                return (
                                                    <button key={t.id}
                                                        onClick={() => navigate(`/boards/${t.board_id}?task=${t.id}`)}
                                                        className="w-full flex items-center gap-2 px-3 py-1.5 rounded bg-[#F8F9FA] hover:bg-[#EEF2FF] text-left transition-colors">
                                                        <div className="flex-1 min-w-0">
                                                            <p className={`text-xs font-semibold truncate ${t.completed_at ? "text-[#9CA3AF] line-through" : "text-[#111827]"}`}>{t.title}</p>
                                                            <p className="text-[10px] text-[#9CA3AF] truncate">{t.board_name} • {t.column_name}</p>
                                                        </div>
                                                        {t.due_date && (
                                                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${overdue ? "bg-red-100 text-red-700" : t.completed_at ? "bg-gray-100 text-gray-500" : "bg-green-100 text-green-700"}`}>
                                                                {new Date(t.due_date).toLocaleDateString("ro-RO", { day: "2-digit", month: "short" })}
                                                            </span>
                                                        )}
                                                    </button>
                                                );
                                            })}
                                            {m.tasks.length > 8 && (
                                                <p className="text-[11px] text-[#9CA3AF] text-center pt-1">+{m.tasks.length - 8} mai multe</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                            {detailedMembers.length === 0 && (
                                <div className="py-10 text-center text-sm text-[#9CA3AF]">Se încarcă...</div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Boards */}
            {team?.boards?.length > 0 && (
                <div>
                    <h2 className="text-xs font-bold text-[#9CA3AF] uppercase tracking-widest mb-3">Board-uri</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {team.boards.map(board => (
                            <button key={board.id} onClick={() => navigate(`/boards/${board.id}`)} data-testid={`team-board-${board.id}`}
                                className="text-left p-4 bg-white border border-[#E4E4E7] rounded-md hover:border-[#002FA7]/30 hover:shadow-sm transition-all">
                                <div className="flex items-center gap-2">
                                    <div className="text-[#002FA7]">{getEntityIcon(board.icon, KanbanSquare, 16)}</div>
                                    <span className="font-semibold text-sm text-[#111827]">{board.name}</span>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Edit Team Modal */}
            {canManage && showEditTeam && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setShowEditTeam(false)} />
                    <div className="relative bg-white rounded-md shadow-xl w-full max-w-md mx-4 z-10">
                        <div className="px-5 py-4 border-b border-[#E4E4E7]">
                            <h2 className="font-bold text-[#111827]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Editează echipa</h2>
                        </div>
                        <form onSubmit={saveTeam} className="px-5 py-4 space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Nume *</label>
                                <input autoFocus data-testid="edit-team-name" value={editForm.name}
                                    onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                                    className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" required />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Descriere</label>
                                <input data-testid="edit-team-description" value={editForm.description}
                                    onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                                    className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
                            </div>
                            <IconPicker value={editForm.icon} onChange={icon => setEditForm({ ...editForm, icon })} />
                            {isAdmin && (team?.members || []).length > 0 && (
                                <div>
                                    <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Team Lead</label>
                                    <select
                                        data-testid="edit-team-lead"
                                        value={editForm.lead_id || ""}
                                        onChange={e => setEditForm({ ...editForm, lead_id: e.target.value })}
                                        className="w-full px-2 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
                                        <option value="">— Fără lead —</option>
                                        {(team?.members || []).filter(m => m.role !== "super_admin").map(m => (
                                            <option key={m.id} value={m.id}>{m.name} ({displayRole(m)})</option>
                                        ))}
                                    </select>
                                    <p className="text-[10px] text-[#9CA3AF] mt-1">Team Lead-ul are drepturi speciale DOAR în această echipă.</p>
                                </div>
                            )}
                            <div className="flex gap-2">
                                <button type="button" onClick={() => setShowEditTeam(false)} className="flex-1 py-2 border border-[#E4E4E7] rounded-md text-sm text-[#4B5563] hover:bg-[#F8F9FA]">Anulează</button>
                                <button data-testid="save-team-btn" type="submit" disabled={saving}
                                    className="flex-1 bg-[#002FA7] text-white py-2 rounded-md text-sm font-semibold hover:bg-[#002482] disabled:opacity-60">
                                    {saving ? "Se salvează..." : "Salvează"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Add Member Modal */}
            {canManage && showAddMember && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/40" onClick={() => { setShowAddMember(false); setSelectedUserIds([]); setMemberSearch(""); }} />
                    <div className="relative bg-white rounded-md shadow-xl w-full max-w-md mx-4 z-10 flex flex-col max-h-[80vh]" data-testid="add-member-modal">
                        <div className="px-5 py-4 border-b border-[#E4E4E7] flex items-center justify-between">
                            <h2 className="font-bold text-[#111827]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Adaugă membri</h2>
                            <span className="text-xs text-[#6B7280]" data-testid="add-member-selected-count">
                                {selectedUserIds.length} selectat{selectedUserIds.length === 1 ? "" : "i"}
                            </span>
                        </div>
                        <form onSubmit={addMember} className="flex flex-col flex-1 min-h-0">
                            <div className="px-5 pt-4 pb-2">
                                <div className="relative">
                                    <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                                    <input
                                        data-testid="add-member-search"
                                        type="text"
                                        placeholder="Caută după nume sau email..."
                                        value={memberSearch}
                                        onChange={e => setMemberSearch(e.target.value)}
                                        autoFocus
                                        className="w-full pl-8 pr-8 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] bg-white"
                                    />
                                    {memberSearch && (
                                        <button type="button" onClick={() => setMemberSearch("")}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#4B5563]">
                                            <X size={14} />
                                        </button>
                                    )}
                                </div>
                                {availableUsers.length > 0 && (
                                    <div className="flex items-center justify-between mt-2.5 text-xs">
                                        <button
                                            type="button"
                                            data-testid="add-member-select-all"
                                            onClick={() => {
                                                const filteredIds = availableUsers
                                                    .filter(u => {
                                                        const q = memberSearch.trim().toLowerCase();
                                                        if (!q) return true;
                                                        return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
                                                    })
                                                    .map(u => u.id);
                                                const allSelected = filteredIds.length > 0 && filteredIds.every(fid => selectedUserIds.includes(fid));
                                                if (allSelected) {
                                                    setSelectedUserIds(prev => prev.filter(pid => !filteredIds.includes(pid)));
                                                } else {
                                                    setSelectedUserIds(prev => Array.from(new Set([...prev, ...filteredIds])));
                                                }
                                            }}
                                            className="text-[#002FA7] font-semibold hover:underline"
                                        >
                                            Selectează / deselectează toți
                                        </button>
                                        {selectedUserIds.length > 0 && (
                                            <button type="button" onClick={() => setSelectedUserIds([])}
                                                className="text-[#6B7280] hover:text-[#111827]">
                                                Resetează
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="flex-1 overflow-y-auto px-3 pb-2" data-testid="add-member-list">
                                {(() => {
                                    const q = memberSearch.trim().toLowerCase();
                                    const filtered = availableUsers.filter(u => {
                                        if (!q) return true;
                                        return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
                                    });
                                    if (availableUsers.length === 0) {
                                        return <div className="px-3 py-6 text-center text-sm text-[#6B7280]">Toți utilizatorii sunt deja membri ai acestei echipe.</div>;
                                    }
                                    if (filtered.length === 0) {
                                        return <div className="px-3 py-6 text-center text-sm text-[#6B7280]">Niciun utilizator nu corespunde căutării.</div>;
                                    }
                                    return filtered.map(u => {
                                        const isSelected = selectedUserIds.includes(u.id);
                                        return (
                                            <label
                                                key={u.id}
                                                data-testid={`add-member-row-${u.id}`}
                                                className={`flex items-center gap-3 px-2 py-2 rounded-md cursor-pointer transition-colors ${isSelected ? "bg-[#EEF2FF]" : "hover:bg-[#F8F9FA]"}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => toggleUserSelection(u.id)}
                                                    data-testid={`add-member-checkbox-${u.id}`}
                                                    className="w-4 h-4 accent-[#002FA7] cursor-pointer"
                                                />
                                                <div className="w-7 h-7 rounded-full bg-[#002FA7] text-white text-xs font-semibold flex items-center justify-center shrink-0">
                                                    {(u.name || u.email || "?").charAt(0).toUpperCase()}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-medium text-[#111827] truncate">{u.name || "(fără nume)"}</div>
                                                    <div className="text-xs text-[#6B7280] truncate">{u.email}</div>
                                                </div>
                                            </label>
                                        );
                                    });
                                })()}
                            </div>
                            <div className="flex gap-2 px-5 py-4 border-t border-[#E4E4E7]">
                                <button type="button" onClick={() => { setShowAddMember(false); setSelectedUserIds([]); setMemberSearch(""); }}
                                    className="flex-1 py-2 border border-[#E4E4E7] rounded-md text-sm text-[#4B5563] hover:bg-[#F8F9FA]">Anulează</button>
                                <button data-testid="add-member-submit" type="submit" disabled={saving || selectedUserIds.length === 0}
                                    className="flex-1 bg-[#002FA7] text-white py-2 rounded-md text-sm font-semibold hover:bg-[#002482] disabled:opacity-60">
                                    {saving ? "Se adaugă..." : selectedUserIds.length > 1 ? `Adaugă (${selectedUserIds.length})` : "Adaugă"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
